import { object, string, z } from 'zod'

const formatPhoneNumber = (phone: string) => {
  return phone.replace(/\D/g, '')
}
export const inquirySchema = object({
  name: string({
    required_error: 'Name is required',
    invalid_type_error: 'Name must be a string',
  }).trim(),
  inquiryType: string({
    required_error: 'Inquiry type is required',
  }),
  phone: z
    .string()
    .optional()
    .refine(
      (val) =>
        val === undefined || val === '' || formatPhoneNumber(val).length >= 10,
      {
        message: 'Entered phone number is not valid',
      },
    )
    .refine(
      (val) =>
        val === undefined ||
        val === '' ||
        /^\+?[1-9]\d{1,14}$/.test(formatPhoneNumber(val)),
      {
        message: 'Entered phone number is not valid',
      },
    ),
  email: z
    .string()
    .optional()
    .refine((val) => val === undefined || val === '' || val.trim().length > 0, {
      message: 'Email cannot be empty',
    })
    .refine(
      (val) => val === undefined || val === '' || val.trim().length <= 70,
      {
        message: 'Email must be less than 70 characters',
      },
    )
    .refine(
      (val) =>
        val === undefined ||
        val === '' ||
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim()),
      {
        message: 'Entered email is not valid',
      },
    ),

  description: string().trim().max(10000).optional(),
  in_party: z
    .preprocess(
      (val) => Number(val),
      z
        .number({
          required_error: 'Headcount is required',
          invalid_type_error: 'Headcount must be a number',
        })
        .nonnegative({ message: 'Headcount must be a positive number' })
        .optional(),
    )
    .optional(),
  datepicker: z.string(),
  start_time: z.date(),
  end_time: z.date(),
  space: string().optional(),
  budget: string({
    required_error: 'Budget is required',
    invalid_type_error: 'Budget must be a string',
  }).optional(),
})
export const inquiryProposalFormSchema = z.object({
  description: z.string().max(500).optional().nullable(),
  payment_name: z.string().min(1, { message: 'Payment name is required' }),
  payment_instructions: z.string().optional().nullable(),
  amount_float: z.preprocess(
    (val) => {
      if (typeof val === 'string') {
        const cleaned = val.replace(/[^0-9.]/g, '')
        return Number(cleaned)
      }
      return val
    },
    z
      .number()
      .positive({ message: 'Budget must be a positive number' })
      .refine(
        (val) => !isNaN(val) && val.toString().replace('.', '').length <= 9,
        {
          message: 'Budget must have max 7 digits and up to 2 decimals',
        },
      ),
  ),
  payment_type: z.string().min(1, { message: 'Payment type is required' }),
  due_date: z
    .string()
    .min(1, { message: 'Authorization due date is required' }),
})
export const inquiryAdditionalQuestionsSchema = z
  .object({
    harder: z.boolean().optional(),
    flow: z.boolean().optional(),
    clicks: z.boolean().optional(),
    structure: z.boolean().optional(),
    other: z.boolean().optional(),
    other_text: z.preprocess(
      (val) => {
        if (typeof val !== 'string') return val
        const trimmed = val.trim()
        return trimmed === '' ? undefined : trimmed
      },
      z
        .string()
        .max(200, 'Maximum 200 characters allowed')
        .regex(/^[a-zA-Z0-9\s.,!?'"()&@\/:;-]+$/, 'Please use only letters, numbers, and common symbols')
        .optional(),
    ),
  })
  .refine(
    (data) => {
      const anyChecked = Object.entries(data).some(
        ([key, value]) => key !== 'other_text' && value === true,
      )

      const hasComment =
        typeof data.other_text === 'string' && data.other_text.trim().length > 0

      return anyChecked || hasComment
    },
    {
      message: 'Please select at least one reason or add a comment',
      path: ['_form'],
    },
  )
